// @ts-nocheck -- Shopify serves this browser asset without a project TypeScript DOM configuration.
(() => {
  if (window['XoHeaderCenteredLogo']) return;

  const DESKTOP_QUERY = '(min-width: 992px)';
  const DEFAULT_GAP = 12;
  const instances = new WeakMap();

  /** @param {Element | null | undefined} element */
  const getRect = (element) => element?.getBoundingClientRect?.() || null;

  /**
   * @param {Element | null | undefined} element
   * @returns {DOMRect[] | null}
   */
  const visibleChildRects = (element) => {
    if (!element) return null;
    /** @type {DOMRect[]} */
    const rects = [];

    for (const child of element.children) {
      const rect = getRect(child);
      const style = window.getComputedStyle(child);
      if (rect && rect.width > 0 && rect.height > 0 && style.visibility !== 'hidden' && style.display !== 'none') rects.push(rect);
    }

    return rects;
  };

  /**
   * @param {Element | null} element
   * @param {DOMRect | { left: number, right: number } | null} navRect
   */
  const firstControlTowardNavRect = (element, navRect) => {
    const rects = visibleChildRects(element);
    if (!rects?.length) return getRect(element);

    if (navRect?.left <= rects[0].left) {
      return rects.reduce((closest, rect) => (rect.left < closest.left ? rect : closest), rects[0]);
    }

    return rects.reduce((closest, rect) => (rect.right > closest.right ? rect : closest), rects[0]);
  };

  /** @param {Element} content */
  const topLevelNavRect = (content) => {
    const desktop = content.querySelector('.xo-header__desktop');
    const desktopRect = getRect(desktop);
    const contentRect = getRect(content);
    const items = content.querySelectorAll('.xo-menu-horizontal__item:not(.xo-menu-horizontal__item--sub)');
    const list = content.querySelector('.xo-menu-horizontal');
    /** @type {DOMRect[]} */
    const rects = [];

    for (const item of items) {
      const rect = getRect(item);
      if (rect && rect.width > 0 && rect.height > 0) rects.push(rect);
    }

    const measuredLeft = rects.length ? Math.min(...rects.map((rect) => rect.left)) : null;
    const measuredRight = rects.length ? Math.max(...rects.map((rect) => rect.right)) : null;
    const listStyles = list instanceof HTMLElement ? getComputedStyle(list) : null;
    const gap = Number.parseFloat(listStyles?.columnGap || listStyles?.gap || '0') || 0;
    const padding = (Number.parseFloat(listStyles?.paddingInlineStart || '0') || 0) + (Number.parseFloat(listStyles?.paddingInlineEnd || '0') || 0);
    const measuredWidth = rects.length ? rects.reduce((total, rect) => total + rect.width, padding + (gap * Math.max(0, rects.length - 1))) : 0;
    const storedWidth = Number.parseFloat(desktop?.dataset.xoHeaderNavWidth || '0');
    const width = Math.max(storedWidth, measuredWidth);

    if (desktop && measuredWidth > storedWidth) desktop.dataset.xoHeaderNavWidth = `${measuredWidth}`;
    if (measuredLeft !== null && measuredRight !== null) return { left: measuredLeft, right: measuredRight, width };
    if (contentRect && width > 0) {
      const isRtl = window.getComputedStyle(content).direction === 'rtl';
      const left = isRtl ? contentRect.right - width : contentRect.left;
      const right = left + width;
      return { left, right, width };
    }

    return desktopRect || getRect(content.querySelector('.xo-header__left'));
  };

  /** @param {Element} content */
  const parseGap = (content) => {
    const value = window.getComputedStyle(content).getPropertyValue('--xo-header-logo-gap').trim();
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : DEFAULT_GAP;
  };

  /** @param {{ header: DOMRect | { left: number, width: number } | null, logo: DOMRect | { width: number } | null, nav: DOMRect | { left: number, right: number } | null, control: DOMRect | { left: number, right: number } | null, gap?: number }} input */
  const compute = ({ header, logo, nav, control, gap = DEFAULT_GAP }) => {
    if (!header || !logo || !nav || !control) return { state: 'idle', shift: 0, availableWidth: null };

    const headerCenter = header.left + (header.width / 2);
    const logoWidth = logo.width || 0;
    const defaultLeft = headerCenter - (logoWidth / 2);
    const defaultRight = headerCenter + (logoWidth / 2);
    const navBeforeControl = nav.left <= control.left;
    const freeStart = navBeforeControl ? nav.right + gap : control.right + gap;
    const freeEnd = navBeforeControl ? control.left - gap : nav.left - gap;
    const rawAvailableWidth = freeEnd - freeStart;
    const availableWidth = Math.max(0, rawAvailableWidth);
    const defaultClear = defaultLeft >= freeStart && defaultRight <= freeEnd;

    if (defaultClear) {
      return {
        state: 'centered', shift: 0, center: headerCenter, left: defaultLeft, right: defaultRight, availableWidth,
        leftClearance: defaultLeft - freeStart, rightClearance: freeEnd - defaultRight,
      };
    }

    const center = freeStart + (availableWidth / 2);
    const renderedWidth = Math.min(logoWidth, availableWidth);
    const left = center - (renderedWidth / 2);
    const right = center + (renderedWidth / 2);

    return {
      state: availableWidth >= logoWidth ? 'offset' : 'constrained', shift: center - headerCenter, center, left, right, availableWidth,
      leftClearance: left - freeStart, rightClearance: freeEnd - right,
    };
  };

  /** @param {Element} content */
  const applyLayout = (content) => {
    const center = content.querySelector('.xo-header__center');
    const logo = content.querySelector('.xo-header__logo');
    const right = content.querySelector('.xo-header__right');
    const media = window.matchMedia(DESKTOP_QUERY);

    if (!media.matches || !center || !logo || !right) {
      content.style.removeProperty('--xo-header-logo-shift');
      content.style.removeProperty('--xo-header-logo-free-width');
      content.classList.remove('xo-header__content--logo-offset', 'xo-header__content--logo-constrained');
      return;
    }

    const headerRect = getRect(content);
    const measuredLogoRect = getRect(logo);
    const storedLogoWidth = Number.parseFloat(logo.dataset.xoHeaderLogoWidth || '0');
    const measuredLogoWidth = measuredLogoRect?.width || 0;
    const logoWidth = Math.max(storedLogoWidth, measuredLogoWidth);
    if (measuredLogoWidth > storedLogoWidth) logo.dataset.xoHeaderLogoWidth = `${measuredLogoWidth}`;
    const logoRect = measuredLogoRect ? { ...measuredLogoRect, width: logoWidth } : null;
    const navRect = topLevelNavRect(content);
    const controlRect = firstControlTowardNavRect(right, navRect);
    const result = compute({ header: headerRect, logo: logoRect, nav: navRect, control: controlRect, gap: parseGap(content) });

    const constrained = result.state === 'constrained';
    content.style.setProperty('--xo-header-logo-shift', `${constrained ? 0 : result.shift || 0}px`);
    if (result.availableWidth === null || constrained) content.style.removeProperty('--xo-header-logo-free-width');
    else content.style.setProperty('--xo-header-logo-free-width', `${result.availableWidth}px`);
    content.classList.toggle('xo-header__content--logo-offset', result.state === 'offset');
    content.classList.toggle('xo-header__content--logo-constrained', constrained);
  };

  /** @param {Element | null} content */
  const initContent = (content) => {
    if (!content || instances.has(content)) return;

    let frame = 0;
    const observed = [content, content.querySelector('.xo-header__desktop'), content.querySelector('.xo-header__center'), content.querySelector('.xo-header__right')].filter(Boolean);
    let disposed = false;
    const schedule = () => {
      if (disposed || frame) return;
      frame = requestAnimationFrame(() => {
        if (disposed) return;
        frame = 0;
        applyLayout(content);
      });
    };

    const observer = new ResizeObserver(schedule);
    for (const element of observed) observer.observe(element);
    const media = window.matchMedia(DESKTOP_QUERY);
    media.addEventListener?.('change', schedule);
    window.addEventListener('resize', schedule, { passive: true });

    const disconnect = () => {
      disposed = true;
      observer.disconnect();
      media.removeEventListener?.('change', schedule);
      window.removeEventListener('resize', schedule);
      if (frame) cancelAnimationFrame(frame);
      instances.delete(content);
    };

    instances.set(content, { disconnect });
    schedule();
    document.fonts?.ready?.then(schedule).catch(() => {});
  };

  /** @param {Document | Element} root */
  const initRoot = (root) => {
    for (const content of root.querySelectorAll('.xo-header__content--reverse')) initContent(content);
  };

  const boot = () => initRoot(document);

  window['XoHeaderCenteredLogo'] = { compute, initRoot };
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof Element) initRoot(event.target);
  });
  document.addEventListener('shopify:section:unload', (event) => {
    if (!(event.target instanceof Element)) return;
    for (const content of event.target.querySelectorAll('.xo-header__content--reverse')) instances.get(content)?.disconnect();
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
